import { ReactNode } from 'react';

import { Button } from '@components';
import { Direction, StatusType, VariantType } from '@enums';
import { cn, colors } from '@utils';

export enum ChipColors {
  primary = 'primary',
  error = 'error',
}

type ChipProps = {
  adornment?: ReactNode;
  direction?: Direction;
  className?: string;
  chipType?: StatusType;
  chipVariant?: VariantType;
  disabled?: boolean;
  disableHover?: boolean;
  smallFont?: boolean;
  onChipClick?: (e?: any) => void;
  rounded?: string;
  color?: keyof typeof colors;
  customizeColor?: boolean;
  withoutPadding?: boolean;
  withoutHover?: boolean;
  withoutScale?: boolean;
  fullWidth?: boolean;
  textClassName?: string;
};

export const Chip = ({
  onChipClick,
  disabled,
  className = '',
  disableHover = false,
  chipType = StatusType.Info,
  chipVariant = VariantType.Contained,
  color,
  withoutScale,
  adornment,
}: ChipProps) => {
  const filledChip = chipVariant == VariantType.Contained;
  const OutlinedChip = chipVariant == VariantType.Outlined;
  const InfoChipType = chipType === StatusType.Info;
  const WarningChipType = chipType === StatusType.Warning;
  const DeleteChipType = chipType === StatusType.Delete;
  const ErrorChipType = chipType === StatusType.Error;
  const SuccessChipType = chipType === StatusType.Success;
  const dynamicClasses = cn(
    'flex focus:outline-none font-light text-xs cursor-default',
    'px-2.5 py-1.5 enabled:shadow-none',
    OutlinedChip && 'ring-1',
    !disableHover && 'hover:bg-opacity-80 hover:text-opacity-80 hover:ring-opacity-80',
    filledChip && InfoChipType && 'bg-theme-primary-main text-white',
    OutlinedChip && InfoChipType && !disabled && 'bg-blue-50 ring-blue-400 text-blue-400',
    OutlinedChip && ErrorChipType && 'bg-red-50 ring-red-400 text-red-400',
    OutlinedChip && SuccessChipType && 'bg-green-50 ring-green-400 text-green-400',
    OutlinedChip && WarningChipType && 'bg-yellow-50 ring-yellow-400 text-yellow-400',
    OutlinedChip && DeleteChipType && 'ring-gray-300 text-gray-400',
    filledChip && ErrorChipType && 'bg-red-500 text-white',
    disabled && 'ring-gray-300 text-opacity-40',
    color && colors[color],
    className,
  );

  return (
    <Button
      onClick={onChipClick}
      disabled={disabled}
      predefinedColor={color}
      className={dynamicClasses}
      adornment={adornment}
      withoutScale={withoutScale}
    />
  );
};
